// FILE: src/hooks/useLearningSession.js
// Drives one learning session for the lesson on screen (plan WP-3):
//   - POST /api/session/start when the lesson opens;
//   - a heartbeat every 30 s, but only while the tab is visible, the lesson
//     has initialized and no blocking dialog (identity challenge, break,
//     daily cap) is open (course-player P17);
//   - an immediate heartbeat when the slide changes, so the server can put
//     the time on the right slide (minimum dwell, D5);
//   - after a hidden or paused stretch the next heartbeat is a "resume", so
//     the server restarts its interval without crediting that stretch;
//   - completion is requested from the server, which may refuse it.
// Every figure shown comes from the server's responses.
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { timeStatusReceived, timeRefused } from "../features/timeSlice";
import { showChallenge } from "../features/course2faSlice";
import {
  startSession,
  heartbeat as postHeartbeat,
  completeSession,
  endSession,
  errorBody
} from "../services/learningSession";

export const HEARTBEAT_MS = 30_000;
const REFUSALS = ["DAILY_LIMIT_REACHED", "BREAK_REQUIRED", "COURSE_EXPIRED", "NOT_ENROLLED"];
const GONE = ["LEARNING_SESSION_ENDED", "LEARNING_SESSION_NOT_FOUND"];

export default function useLearningSession({ resourceId, enabled = true }) {
  const dispatch = useDispatch();
  const store = useStore();
  const challengeOpen = useSelector((s) => Boolean(s.course2fa?.required));
  const notice = useSelector((s) => s.time?.notice || null);
  const breakRequired = useSelector((s) => Boolean(s.time?.breakRequired));

  const [phase, setPhase] = useState("idle"); // idle | starting | active | blocked | error
  const [blockedCode, setBlockedCode] = useState(null);
  const [sessionSec, setSessionSec] = useState(0);
  const [completion, setCompletion] = useState(null);

  const sessionRef = useRef(null);
  const locationRef = useRef(null);
  const sentLocationRef = useRef(null);
  const initializedRef = useRef(false);
  const inFlightRef = useRef(null);
  const againRef = useRef(false);
  const resumeRef = useRef(false); // next heartbeat must not credit the gap
  const restartsRef = useRef(0);
  const paused = challengeOpen || notice !== null;
  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  const apply = useCallback(
    (body) => {
      if (!body) return;
      dispatch(timeStatusReceived(body));
      if (body.challengeDue && body.challenge && !store.getState().course2fa?.required) {
        dispatch(showChallenge(body.challenge));
      }
      if (typeof body.sessionCreditedSeconds === "number") setSessionSec(body.sessionCreditedSeconds);
      if (body.completion) setCompletion(body.completion);
      // This heartbeat reached the cap or the 2-hour mark: the lesson closes.
      if (!body.untimed && (body.capped || body.breakRequired) && sessionRef.current) {
        setBlockedCode(body.capped ? "DAILY_LIMIT_REACHED" : "BREAK_REQUIRED");
        setPhase("blocked");
      }
    },
    [dispatch, store]
  );

  const refuse = useCallback(
    (body) => {
      if (REFUSALS.includes(body?.code)) {
        dispatch(timeRefused(body));
        setBlockedCode(body.code);
        setPhase("blocked");
        return true;
      }
      return false;
    },
    [dispatch]
  );

  const beatRef = useRef(() => {});
  const start = useCallback(async () => {
    setPhase("starting");
    setBlockedCode(null);
    try {
      const body = await startSession(resourceId);
      sessionRef.current = body.sessionId;
      sentLocationRef.current = null;
      resumeRef.current = false;
      setSessionSec(0);
      setPhase("active");
      apply(body);
      if (initializedRef.current) beatRef.current();
    } catch (err) {
      if (!refuse(errorBody(err))) setPhase("error");
    }
  }, [resourceId, apply, refuse]);

  const beat = useCallback(
    async ({ closing = false } = {}) => {
      const id = sessionRef.current;
      if (!id || !initializedRef.current) return;
      const visible = document.visibilityState === "visible";
      if (!closing && (!visible || pausedRef.current)) {
        resumeRef.current = true; // whatever happens next, this stretch isn't credited
        return;
      }
      if (inFlightRef.current) {
        againRef.current = true;
        return inFlightRef.current;
      }
      const location = locationRef.current;
      const resume = resumeRef.current;
      resumeRef.current = false;
      const run = (async () => {
        try {
          const body = await postHeartbeat(id, location, resume);
          sentLocationRef.current = location;
          apply(body);
        } catch (err) {
          const body = errorBody(err);
          if (GONE.includes(body?.code) && restartsRef.current < 3) {
            // Another tab (or a new login) took over: open a fresh session here.
            restartsRef.current += 1;
            sessionRef.current = null;
            start();
          } else refuse(body);
        } finally {
          inFlightRef.current = null;
          if (againRef.current) {
            againRef.current = false;
            beatRef.current();
          }
        }
      })();
      inFlightRef.current = run;
      return run;
    },
    [apply, refuse, start]
  );
  beatRef.current = beat;

  // Open the session when the lesson is shown; end it when the page goes.
  useEffect(() => {
    if (!enabled || !resourceId) return undefined;
    start();
    const end = () => sessionRef.current && endSession(sessionRef.current);
    window.addEventListener("pagehide", end);
    return () => {
      window.removeEventListener("pagehide", end);
      end();
      sessionRef.current = null;
    };
  }, [enabled, resourceId, start]);

  // Regular heartbeat + immediate ones on focus / visibility changes.
  useEffect(() => {
    if (phase !== "active") return undefined;
    const t = setInterval(() => beat(), HEARTBEAT_MS);
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        resumeRef.current = true; // hidden time never counts
        beat();
      } else {
        beat({ closing: true }); // credit up to the moment it was hidden
      }
    };
    const onFocus = () => beat();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
    };
  }, [phase, beat]);

  // A dialog opened: close the interval now. It closed: resume without credit.
  useEffect(() => {
    if (phase !== "active") return;
    if (paused) {
      Promise.resolve(beatRef.current({ closing: true })).finally(() => {
        resumeRef.current = true;
      });
    } else if (resumeRef.current) beatRef.current();
  }, [paused, phase]);

  // The break countdown ended (BreakDialog): try again.
  useEffect(() => {
    if (enabled && phase === "blocked" && blockedCode === "BREAK_REQUIRED" && !breakRequired) start();
  }, [enabled, phase, blockedCode, breakRequired, start]);

  const setInitialized = useCallback(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    beat();
  }, [beat]);

  const setLocation = useCallback(
    (index) => {
      const n = Number(index);
      if (!Number.isInteger(n) || n < 1) return;
      locationRef.current = n;
      if (!initializedRef.current) initializedRef.current = true;
      if (sentLocationRef.current !== n) beat();
    },
    [beat]
  );

  const requestCompletion = useCallback(async () => {
    const id = sessionRef.current;
    if (!id) return null;
    await beat(); // the time on the slide just finished counts first
    try {
      const body = await completeSession(id);
      setCompletion(body);
      return body;
    } catch (err) {
      const body = errorBody(err);
      if (body?.code === "COMPLETION_REJECTED_MIN_TIME") {
        const verdict = { completed: false, ...body };
        setCompletion(verdict);
        return verdict;
      }
      refuse(body);
      return null;
    }
  }, [beat, refuse]);

  return {
    phase,
    blockedCode,
    sessionSec,
    completion,
    setInitialized,
    setLocation,
    requestCompletion,
    sessionId: sessionRef.current
  };
}
